import AsyncStorage from '@react-native-async-storage/async-storage';
import * as BackgroundFetch from 'expo-background-fetch';
import * as TaskManager from 'expo-task-manager';
import { isNative } from '@/lib/platform';

import { queryClient } from './queryClient';
import { trpc } from './trpc';
import { primeLeagueCache } from './leagueCache';
import { setupQueryPersistence } from './queryPersistence';
import { getRecentLeagueIds } from './storage';
import { clientLogger } from './datadog';
import type { LeagueSummary } from '@/types/league';

const TASK_NAME = 'LEAGUE_BACKGROUND_REFRESH';
const LAST_SUCCESS_KEY = 'backgroundRefresh:lastSuccessAt';
const MAX_LEAGUE_DETAILS = 3;

const isNativePlatform = isNative;

async function persistLastSuccessTimestamp() {
  await AsyncStorage.setItem(LAST_SUCCESS_KEY, new Date().toISOString());
}

async function pickLeagueIds(leagues: any[] | undefined) {
  const recent = await getRecentLeagueIds();
  if (recent.length) {
    return recent.slice(0, MAX_LEAGUE_DETAILS);
  }

  if (!Array.isArray(leagues)) {
    return [];
  }

  return leagues
    .filter((league) => league?.id !== undefined)
    .slice(0, MAX_LEAGUE_DETAILS)
    .map((league) => String(league.id));
}

async function hydrateLeagueDetails(leagueIds: string[], knownLeagues: LeagueSummary[]) {
  for (const leagueId of leagueIds) {
    try {
      const leagueMatch = knownLeagues.find((league) => String(league.id) === leagueId);
      const league = await queryClient.fetchQuery(
        trpc.leagues.detail.queryOptions({
          leagueId,
          seasonId: leagueMatch?.resolvedSeasonId ?? undefined,
        }),
      );
      primeLeagueCache(queryClient, league as unknown as LeagueSummary);
    } catch (error) {
      clientLogger.warn('[background-refresh] Failed to hydrate league', {
        leagueId,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
}

async function runRefreshTask() {
  await setupQueryPersistence();

  const leagues = (await queryClient.fetchQuery(
    trpc.leagues.list.queryOptions(),
  )) as unknown as LeagueSummary[];
  leagues.forEach((league) => primeLeagueCache(queryClient, league));
  const defaultLeagueId = getDefaultLeagueId(leagues);

  const targetLeagueIds = await pickLeagueIds(leagues);
  if (defaultLeagueId && !targetLeagueIds.includes(defaultLeagueId)) {
    targetLeagueIds.unshift(defaultLeagueId);
  }
  if (targetLeagueIds.length) {
    await hydrateLeagueDetails(targetLeagueIds, leagues);
  }

  await hydrateDefaultLeagueCurrentWeekGames(defaultLeagueId);

  await persistLastSuccessTimestamp();
}

async function hydrateDefaultLeagueCurrentWeekGames(defaultLeagueId: string | null) {
  if (!defaultLeagueId) {
    return;
  }

  try {
    const currentWeek = await queryClient.fetchQuery(trpc.nfl.currentWeek.queryOptions());
    if (!currentWeek?.id) {
      return;
    }

    await queryClient.fetchQuery(
      trpc.nfl.gamesByWeek.queryOptions({
        weekId: String(currentWeek.id),
      }),
    );
  } catch (error) {
    clientLogger.warn('[background-refresh] Failed to hydrate current week games', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

if (isNativePlatform) {
  TaskManager.defineTask(TASK_NAME, async () => {
    try {
      await runRefreshTask();
      return BackgroundFetch.BackgroundFetchResult.NewData;
    } catch (error) {
      if (__DEV__) {
        clientLogger.error('[background-refresh] Task failed', {
          error: error instanceof Error ? error.message : String(error),
        });
      }
      // 401s or connectivity errors should not crash the app; signal failure so OS can retry later.
      return BackgroundFetch.BackgroundFetchResult.Failed;
    }
  });
}

export async function initializeBackgroundRefresh() {
  if (!isNativePlatform) {
    return;
  }

  try {
    const status = await BackgroundFetch.getStatusAsync();
    if (
      status === BackgroundFetch.BackgroundFetchStatus.Restricted ||
      status === BackgroundFetch.BackgroundFetchStatus.Denied
    ) {
      if (__DEV__) {
        clientLogger.info('[background-refresh] Background fetch disabled by OS');
      }
      return;
    }

    const isRegistered = await TaskManager.isTaskRegisteredAsync(TASK_NAME);
    if (!isRegistered) {
      await BackgroundFetch.registerTaskAsync(TASK_NAME, {
        minimumInterval: 15 * 60, // 15 minutes
        stopOnTerminate: false,
        startOnBoot: true,
      });
      if (__DEV__) {
        clientLogger.info('[background-refresh] Registered background refresh task');
      }
    }
  } catch (error) {
    clientLogger.warn('[background-refresh] Failed to register background fetch task', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

export async function getLastBackgroundRefreshTimestamp(): Promise<string | null> {
  if (!isNativePlatform) {
    return null;
  }

  return AsyncStorage.getItem(LAST_SUCCESS_KEY);
}

function getDefaultLeagueId(leagues: any[] | undefined): string | null {
  if (!Array.isArray(leagues) || !leagues.length) {
    return null;
  }

  const firstLeague = leagues[0];
  if (!firstLeague || firstLeague.id === undefined || firstLeague.id === null) {
    return null;
  }

  return String(firstLeague.id);
}
